/**
 * Learning API types — manually extracted from generated GraphQL operations.ts.
 * These match the shapes returned by the Hasura GraphQL API (proxied via /api/learner/).
 * Deduplicated: shared sub-types are defined once and referenced.
 */

// --- Shared sub-types ---

export type LearningUpload = {
  __typename?: 'learning_uploads'
  id: number
  path: string
}

export type LearningLibrary = {
  __typename?: 'groups'
  id: string
  slug: string
  title: string
  learning_subscriptions_enabled: boolean
}

export type AggregateCount = {
  aggregate?: { count: number } | null
}

export type ActiveSubscription = {
  __typename?: 'user_group_members'
  created_at: any
  expires_at?: any | null
}

// --- Fragment types ---

export type PageWithPositionFragment = {
  __typename?: 'learning_module_revision_pages'
  position: number
  page: {
    __typename?: 'learning_pages'
    id: string
    title?: string | null
    body: any
  }
}

export type SeriesSubscriptionSettingsFragment = {
  __typename?: 'learning_series_subscription_settings'
  module_price: any
  series_price: any
  available_as_module: boolean
  available_as_series: boolean
  subscription_interval?: string | null
  created_at: any
  created_by: number
  currency_code: string
  updated_at: any
  updated_by: number
}

export enum Learning_Series_Subscription_Settings_Discount_Type_Enum {
  FreeAccess = 'free_access',
  PaidByPartner = 'paid_by_partner',
  Percentage = 'percentage',
}

export type SeriesDiscountSettingsFragment = {
  __typename?: 'learning_series_discount_settings'
  created_at: any
  created_by: number
  discount_percentage: any
  discount_type: Learning_Series_Subscription_Settings_Discount_Type_Enum
  series_id: any
  updated_by: number
  updated_at: any
  discount_groups: Array<{
    __typename?: 'learning_series_discount_groups'
    group_id: string
  }>
}

export type ModuleRevisionResponseFragment = {
  __typename?: 'learning_module_revisions'
  id: any
  module_id: any
  status: any
  slug: string
  title: string
  description?: string | null
  rich_description?: string | null
  cover_image_id?: number | null
  cover_logo_id?: number | null
  card_thumbnail_id?: number | null
  cover_theme?: string | null
  cover_author?: string | null
  expected_completion_time_minutes?: number | null
  want_page_titles: boolean
  mcq_settings_enabled: boolean
  mcq_settings_feedback?: string | null
  mcq_settings_labels: string
  mcq_settings_retry: any
  mcq_settings_retry_limit?: any | null
  mcq_settings_show_score: boolean
  mcq_settings_pass_mark_type: any
  mcq_settings_pass_mark?: number | null
  cme_expires_after_seconds?: number | null
  cme_expires_at?: any | null
  cme_credits_description?: string | null
  cme_reference?: string | null
  cme_accreditor?: string | null
  objectives?: any | null
  settings?: any | null
  contains_mcq?: boolean | null
  feedback_form_id?: any | null
  categories?: any | null
  created_at: any
  redirect_url?: string | null
  module: { __typename?: 'learning_modules'; slug?: string | null }
  cover_image?: LearningUpload | null
  cover_logo?: LearningUpload | null
  card_thumbnail?: LearningUpload | null
  pages: Array<PageWithPositionFragment>
  preview_page: Array<{
    __typename?: 'learning_module_revision_pages'
    page: {
      __typename?: 'learning_pages'
      id: string
      title?: string | null
      body: any
    }
  }>
}

export type SessionFeedbackResponse = {
  __typename?: 'learning_learner_session_feedback'
  question_id?: any | null
  feedback?: string | null
  answers?: any | null
}

export type ActiveSessionWithResponsesFragment = {
  __typename?: 'learning_learner_sessions'
  id: any
  started_at: any
  marks?: number | null
  marks_available?: number | null
  active: boolean
  status?: string | null
  revision: ModuleRevisionResponseFragment
  responses: Array<SessionFeedbackResponse>
}

export type FinishedSessionWithResponsesFragment =
  ActiveSessionWithResponsesFragment & {
    finished_at?: any | null
  }

export type LearnerSessionEventFragment = {
  __typename?: 'learning_learner_event_log'
  id: number
  time: any
  marks?: number | null
  question_id?: any | null
  event: any
  session: FinishedSessionWithResponsesFragment
}

export type LearnerRatingQuestionsFragment = {
  __typename?: 'learning_learner_module_rating_questions'
  id: any
  position: number
  question_text: string
  answer_type: string
  series_id?: any | null
  module_id?: any | null
}

/** Shared module meta fields used by ModulePublished, ModuleResponseAndCompletion, SeriesWithModules, LibraryContent */
type ModuleMetaFields = {
  __typename?: 'learning_modules'
  id: any
  slug?: string | null
  template_type?: string | null
  series_id: any
  status: any
  author_id: number
  position?: number | null
  has_subscription?: boolean | null
  price?: any | null
  ever_completed?: boolean | null
  has_passed?: boolean | null
  failed_attempts?: number | null
  reached_max_attempts?: boolean | null
  can_edit?: boolean | null
  can_publish?: boolean | null
  can_approve?: boolean | null
}

/** Shared series fields included on modules */
type ModuleSeriesFields = {
  __typename?: 'learning_series'
  id: any
  title: string
  slug: string
  series_type?: string | null
  library_type: string
  library_id: string
  sort_modules_by: any
  force_completion_order: boolean
  can_edit?: boolean | null
  payments_enabled: boolean
  has_subscription?: boolean | null
  accreditation_when: string
  certificate_template: string
  certificate_content?: any | null
  cme_expires_after_seconds?: number | null
  cme_expires_at?: any | null
  cme_credits_description?: string | null
  cme_reference?: string | null
  cme_accreditor?: string | null
  library?: LearningLibrary | null
  subscription_settings?: SeriesSubscriptionSettingsFragment | null
}

export type ModulePublishedResponseFragment = ModuleMetaFields & {
  revision?: ModuleRevisionResponseFragment | null
  author: { __typename?: 'users'; name: string }
  series: ModuleSeriesFields
  published_revision?: {
    __typename?: 'learning_module_revisions'
    id: any
  } | null
  active_subscription: Array<ActiveSubscription>
}

export type ModuleResponseAndCompletionFragment = ModuleMetaFields & {
  reflections: Array<{
    __typename?: 'learning_learner_reflections'
    body: string
  }>
  ratings: Array<{
    __typename?: 'learning_learner_module_ratings'
    responses_aggregate: AggregateCount
  }>
  latestSession: Array<{
    __typename?: 'learning_learner_sessions'
    last_event_at: any
    marks?: number | null
    marks_available?: number | null
    finished_at?: any | null
    id: any
    started_at: any
    active: boolean
    status?: string | null
    events: Array<{
      __typename?: 'learning_learner_event_log'
      event: any
      time: any
    }>
    revision: ModuleRevisionResponseFragment
    responses: Array<SessionFeedbackResponse>
  }>
  everCompleted: Array<{
    __typename?: 'learning_learner_sessions'
    finished_at?: any | null
  }>
  revision?: ModuleRevisionResponseFragment | null
  author: { __typename?: 'users'; name: string }
  series: ModuleSeriesFields
  published_revision?: {
    __typename?: 'learning_module_revisions'
    id: any
  } | null
  active_subscription: Array<ActiveSubscription>
}

export type MinimalModuleListItemFragment = {
  __typename?: 'learning_modules'
  id: any
  slug?: string | null
  position?: number | null
  ever_completed?: boolean | null
  has_passed?: boolean | null
  series: {
    __typename?: 'learning_series'
    library_id: string
    payments_enabled: boolean
    accreditation_when: string
    slug: string
    id: any
    library?: { __typename?: 'groups'; slug: string; id: string } | null
  }
  published_revision?: {
    __typename?: 'learning_module_revisions'
    title: string
  } | null
}

export type SeriesResponseFragment = {
  __typename?: 'learning_series'
  id: any
  series_type?: string | null
  library_type: string
  library_id: string
  slug: string
  title: string
  display_author?: string | null
  description?: string | null
  rich_description?: string | null
  sort_modules_by: any
  cme_accreditor?: string | null
  payments_enabled: boolean
  has_subscription?: boolean | null
  force_completion_order: boolean
  accreditation_when: string
  library?: LearningLibrary | null
  image?: LearningUpload | null
  star_rating?: {
    __typename?: 'learning_series_star_ratings'
    rating?: any | null
  } | null
  editor?: { __typename?: 'users'; id: number; name: string } | null
  subscription_settings?: SeriesSubscriptionSettingsFragment | null
  module_count: AggregateCount
  completed_count: AggregateCount
  active_subscription: Array<ActiveSubscription>
}

export type SeriesWithModulesFragment = SeriesResponseFragment & {
  modules: Array<ModulePublishedResponseFragment>
}

// --- Query response types ---

export type LibraryContentQuery = {
  __typename?: 'query_root'
  series: Array<SeriesWithModulesFragment>
}
